<template>
  <div>
    <div class="summary-bar d-flex align-items-center mb-3">
      <div class="summary-item">
        <div class="summary-value">
          {{ payerIds.length }}
        </div>
        <div class="summary-label text-secondary">
          {{ $t('menu.payers') }}
        </div>
      </div>
      <div class="summary-item">
        <div class="summary-value">
          {{ totalBillsCount }}
        </div>
        <div class="summary-label text-secondary">
          {{ $t('bill.unpaidCount') }}
        </div>
      </div>
      <div class="summary-item">
        <div class="summary-value">
          {{ formatMoney(totalAmountDue, 'PLN') }}
        </div>
        <div class="summary-label text-secondary">
          {{ $t('bill.toPay') }}
        </div>
      </div>
    </div>
    <hr class="mt-0 mb-3">

    <div class="pills-container mb-3">
      <div
        v-for="id in payerIds"
        :key="'pill-' + id"
        class="payer-pill"
        :class="{ active: activePayerId === id }"
        @click="activePayerId = id"
      >
        <div class="pill-name">
          {{ payerName(id) }}
        </div>
        <div class="pill-meta">
          {{ payerBills(id).length }} · {{ formatMoney(payerAmountDue(id), 'PLN') }}
        </div>
      </div>
    </div>

    <template v-if="activePayerId">
      <template v-if="payerDetails[activePayerId]">
        <PayerHeader
          :type="payerDetails[activePayerId].type"
          :name="payerDetails[activePayerId].name"
          :code="payerDetails[activePayerId].code"
          :number="payerDetails[activePayerId].number"
          class="px-2 pb-2"
        />
        <PayerDataSummary
          v-bind="payerDetails[activePayerId]"
        />

        <h6 class="bill-section-title mt-3 mb-2">
          {{ $t('bill.toPay') }}
        </h6>
        <hr class="mt-0 mb-2">

        <div
          v-for="b in payerBills(activePayerId)"
          :key="b.billId"
          class="bill-row d-flex align-items-center"
        >
          <div
            class="color-dot mr-2"
            :style="{ 'background-color': categoryColor(b.categoryId) }"
          />
          <div class="bill-info flex-grow-1">
            <div class="bill-name">
              {{ b.title }}
            </div>
            <div class="bill-meta text-secondary">
              <span>{{ categoryName(b.categoryId) }}</span>
              <span class="mx-1">·</span>
              <span>{{ formatDate(b.billingDay) }}</span>
            </div>
          </div>
          <div class="bill-right text-right">
            <div class="bill-amount money-font">
              {{ formatMoney(b.amount - b.paid, b.currency) }}
            </div>
            <div
              class="bill-due small"
              :class="dueDaysClass(b.dueDate)"
            >
              {{ formatDate(b.dueDate) }}
              <span class="due-days">
                ({{ dueDaysLabel(b.dueDate) }})
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="payerBills(activePayerId).length === 0"
          class="small text-center text-secondary py-2"
        >
          {{ $t('bill.noneToPay') }}
        </div>
      </template>
      <Loader v-else />
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import PayerHeader from './PayerHeader';
import PayerDataSummary from './sections/PayerDataSummary';

export default {
  props: {
    payer: Object,
  },
  data: () => ({
    payerDetails: {},
    activePayerId: null,
  }),
  components: {
    PayerHeader,
    PayerDataSummary,
  },
  computed: {
    ...mapGetters(['formatMoney', 'payee', 'categoryColor']),
    payerIds() {
      return this.payer.payerIds
        .filter((id) => this.payerBills(id).length > 0);
    },
    allBills() {
      return this.payerIds.flatMap((id) => this.payerBills(id));
    },
    totalBillsCount() {
      return this.allBills.length;
    },
    totalAmountDue() {
      return this.allBills.reduce((sum, b) => sum + (b.amount - b.paid), 0);
    },
  },
  watch: {
    payerIds: {
      immediate: true,
      handler(ids) {
        ids.forEach((id) => this.fetchDetails(id));
        if (ids.length > 0 && (!this.activePayerId || !ids.includes(this.activePayerId))) {
          [this.activePayerId] = ids;
        }
      },
    },
  },
  methods: {
    ...mapActions(['getPayerDetails']),
    fetchDetails(payerId) {
      if (this.payerDetails[payerId]) return;
      this.getPayerDetails({
        params: {
          payerId,
          query: {
            withBills: false,
            withActivation: false,
          },
        },
      }).then(({ data }) => {
        this.$set(this.payerDetails, payerId, data);
      });
    },
    payerBills(payerId) {
      return (this.payer.bills.filter((x) => x.payerId === payerId) || []);
    },
    payerAmountDue(payerId) {
      return this.payerBills(payerId).reduce((sum, b) => sum + (b.amount - b.paid), 0);
    },
    payerName(id) {
      if (this.payerDetails[id]) return this.payerDetails[id].name;
      return '...';
    },
    categoryName(categoryId) {
      const cat = (this.payee?.categories || []).find((x) => x.id === categoryId);
      return cat ? cat.name : '';
    },
    formatDate(date) {
      return moment(date).format('D MMM YYYY');
    },
    dueDays(dueDate) {
      return moment().diff(moment(dueDate), 'days');
    },
    dueDaysLabel(dueDate) {
      const days = this.dueDays(dueDate);
      if (days > 0) return `${days}d`;
      if (days === 0) return this.$t('general.today');
      return `${Math.abs(days)}d`;
    },
    dueDaysClass(dueDate) {
      const days = this.dueDays(dueDate);
      if (days > 0) return 'text-danger';
      return 'text-success';
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-bar {
  gap: 24px;
}

.summary-item {
  text-align: center;
}

.summary-value {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
}

.summary-label {
  font-size: 11px;
}

.pills-container {
  gap: 6px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.payer-pill {
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 150ms;

  &:hover {
    background-color: #f0f0f0;
  }

  &.active {
    background-color: rgba($blue, 0.2);
    border-color: rgba($blue, 0.3);
  }
}

.pill-name {
  font-weight: 500;
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pill-meta {
  font-size: 10px;
  white-space: nowrap;
}

.bill-section-title {
  font-size: 14px;
  font-weight: 600;
}

.color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.bill-row {
  padding: 6px 8px;
  border-radius: 8px;
  margin-bottom: 4px;

  &:hover {
    background-color: #f8f8f8;
  }
}

.bill-name {
  font-weight: 500;
  font-size: 13px;
  line-height: 1.3;
}

.bill-meta {
  font-size: 11px;
}

.bill-amount {
  font-weight: 600;
  font-size: 13px;
}

.bill-due {
  font-size: 11px;
}

.due-days {
  font-weight: 600;
}
</style>
